// src/pages/orders/CreateOrderPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  ShoppingCart, 
  X, 
  Store,
  User,
  Package,
  CreditCard,
  FileText,
  CheckCircle
} from 'lucide-react';
import { createEcomOrder } from '../../services/api/orders';
import { getMyUsage } from '../../services/api/subscription';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../hooks/useAuth';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import QuotaWarning from '../../components/common/Subscription/QuotaWarning';
import useOrderForm from '../../hooks/useOrderForm';
import ShopSelectionSection from './components/ShopSelectionSection';
import CustomerInformationSection from './components/CustomerInformationSection';
import OrderItemsSection from './components/OrderItemsSection';
import OrderStatusSection from './components/OrderStatusSection';
import PaymentRecordsSection from './components/PaymentRecordsSection';
import OrderSummarySection from './components/OrderSummarySection';
import OrderOriginSection from './components/OrderOriginSection';
import ShiftPickerSection from './components/ShiftPickerSection';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import DatePicker from '../../components/ui/DatePicker/DatePicker';
import styles from './CreateOrderPage.module.css';

const CreateOrderPage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  const { user } = useAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [isLoadingQuota, setIsLoadingQuota] = useState(true);

  const { settings: crossSellingSettings } = useBusinessSettings('crossSelling');
  const { settings: messageCenterSettings } = useBusinessSettings('messageCenter');

  const {
    formData,
    requiredFields,
    referenceData,
    selectableOrderStatuses,
    userShops,
    selectedShop,
    isLoading,
    isCalculatingDelivery,
    errors,
    orderTotals,
    replacedOrderData,
    isLookingUpOrder,
    orderLookupError,
    canChangeUnitPrice,
    canGiveDiscount,
    canChangeDeliveryCharge,
    handleShopChange,
    handleFieldChange,
    handlePhoneNumberChange,
    handlePhoneValidationChange,
    handleWeightChange,
    togglePrimaryPhone,
    addPhoneNumber,
    removePhoneNumber,
    addOrderItem,
    updateOrderItem,
    removeOrderItem,
    updateBundleQuantity,
    removeBundle,
    setBundleCrossSell,
    updateReturnItemQty,
    addPaymentRecord,
    updatePaymentRecord,
    removePaymentRecord,
    validateForm,
    getOrderNumberPreview,
    requiresPaymentRecords,
    allowsOptionalPaymentRecords,
    isReplacementOrder,
    isStorePickupOrder,
    getStatusReasons,
    autofillCustomer,
    revertAutofill,
    clearOrderItems,
  } = useOrderForm();

  const handleShopSelect = async (shopId) => {
    if (shopId !== formData.shopId && formData.orderItems.length > 0) {
      const confirmed = await confirm(t('ecomOrders.shopChangeClearsItems'), {
        title: t('ecomOrders.shopChangeTitle'),
        confirmText: t('common.continue'),
        cancelText: t('common.cancel'),
      });
      if (!confirmed) return;
      clearOrderItems();
    }
    handleShopChange(shopId);
  };

  useEffect(() => {
    const fetchQuota = async () => {
      try {
        setIsLoadingQuota(true);
        const response = await getMyUsage();
        setQuotaInfo(response.usage?.ecomOrders || null);
      } catch (err) {
        console.error('Failed to fetch quota:', err);
      } finally {
        setIsLoadingQuota(false);
      }
    };
    fetchQuota();
  }, []);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!validateForm()) {
      error(t('ecomOrders.someFieldsRequired'), {
        title: t('common.validationError'),
        duration: 5000
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const orderData = {
        customerName: formData.customerName.trim(),
        address: formData.address.trim(),
        cityId: formData.cityId,
        phoneNumbers: formData.phoneNumbers.filter(p => p.phoneNumber.trim()),
        shopId: formData.shopId,
        orderStatusKey: formData.orderStatusKey,
        paymentTypeKey: formData.paymentTypeKey,
        orderItems: formData.orderItems.map(item => ({
          productId: item.productId,
          variationId: item.variationId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          lineDiscount: item.lineDiscount || 0,
          overrideDeliveryCharge: item.overrideDeliveryCharge ?? null,
          ...(item.bundleGroupKey && {
            bundleProductId: item.bundleProductId,
            bundleGroupKey: item.bundleGroupKey,
            bundleQuantity: item.bundleQuantity,
          }),
          ...(crossSellingSettings.enabled && item.isCrossSell && {
            isCrossSell: true,
            crossSellQuantity: item.crossSellQuantity || item.quantity,
          }),
        })),
        deliveryCharge: formData.deliveryCharge
      };

      orderData.weight = formData.weight;
      if (formData.orderOriginKey) orderData.orderOriginKey = formData.orderOriginKey;
      if (messageCenterSettings.enabled && selectedShop?.messageCenterShiftEligible && formData.messageCenterShiftId) {
        orderData.messageCenterShiftId = formData.messageCenterShiftId;
      }

      if (formData.privateReference.trim()) orderData.privateReference = formData.privateReference.trim();
      if (formData.customerEmail.trim()) orderData.customerEmail = formData.customerEmail.trim();
      if (formData.deliveryNotes.trim()) orderData.deliveryNotes = formData.deliveryNotes.trim();
      if (formData.specialNotes.trim()) orderData.specialNotes = formData.specialNotes.trim();
      if (formData.orderStatusReasonKey) orderData.orderStatusReasonKey = formData.orderStatusReasonKey;
      if (formData.holdUntilDate) orderData.holdUntilDate = formData.holdUntilDate;

      if (isReplacementOrder()) {
        if (formData.replacedOrderId) orderData.replacedOrderId = formData.replacedOrderId;
        if (formData.replacementReasonKey) orderData.replacementReasonKey = formData.replacementReasonKey;
        if (formData.replacementCodAmount !== null && formData.replacementCodAmount !== undefined) {
          orderData.replacementCodAmount = parseFloat(formData.replacementCodAmount);
        }
        if (formData.returnAirwaybillNumber?.trim()) {
          orderData.returnAirwaybillNumber = formData.returnAirwaybillNumber.trim();
        }
        const selectedReturnItems = (formData.returnItems || []).filter(item => item.returnQty > 0);
        if (selectedReturnItems.length > 0) {
          orderData.returnItems = selectedReturnItems.map(item => ({
            replacedOrderItemId: item.replacedOrderItemId,
            returnQuantity: item.returnQty,
          }));
        }
      }

      if ((requiresPaymentRecords() || allowsOptionalPaymentRecords()) && formData.paymentRecords.length > 0) {
        orderData.paymentRecords = formData.paymentRecords.map(record => ({
          paymentMethodKey: record.paymentMethodKey,
          paymentAmount: parseFloat(record.paymentAmount),
          reference: record.reference || '',
          proofUrl: record.proofUrl || null,
          paymentDate: record.paymentDate
        }));
      }

      const response = await createEcomOrder(orderData);
      const createdOrder = response.order;

      success(t('ecomOrders.orderCreatedSuccess', {
        orderNumber: createdOrder.orderNumber
      }), {
        title: t('common.success'),
        duration: 15000,
        actions: [
          {
            label: t('common.copy'),
            type: 'secondary',
            onClick: () => {
              navigator.clipboard.writeText(createdOrder.orderNumber)
                .then(() => success(t('common.copiedToClipboard'), { duration: 2500 }))
                .catch(() => error(t('common.copyFailed')));
            }
          }
        ]
      });

      navigate(`/orders/${createdOrder.id}/edit`);
    } catch (err) {
      console.error('Order creation error:', err);
      const errorMessage = err.response?.data?.message || t('ecomOrders.failedToCreateOrder');
      error(errorMessage, {
        title: t('common.error'),
        duration: 6000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getQuotaSeverity = () => {
    if (!quotaInfo || quotaInfo.isUnlimited) return 'info';
    const percentage = quotaInfo.limit > 0 ? (quotaInfo.usage / quotaInfo.limit) * 100 : 0;
    if (percentage >= 90) return 'critical';
    if (percentage >= 80) return 'warning';
    return 'info';
  };

  const isQuotaExceeded = () => {
    if (!quotaInfo || quotaInfo.isUnlimited) return false;
    return quotaInfo.usage >= quotaInfo.limit;
  };

  const formatGrandTotal = (amount) => {
    if (!amount) return null;
    return `Rs. ${parseFloat(amount).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Derived state for conditional action bar fields
  const selectedStatus = referenceData.orderStatuses?.find(s => s.key === formData.orderStatusKey);
  const resolvedStatusReasons = getStatusReasons();

  if (isLoading) {
    return (
      <div className={styles.createOrderPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <ShoppingCart className={styles.headerIcon} size={22} />
              <div className={styles.titleContent}>
                <h1>{t('ecomOrders.createNewOrder')}</h1>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge}></div>
            <h3>{t('ecomOrders.loadingReferenceData')}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.createOrderPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <ShoppingCart className={styles.headerIcon} size={22} />
            <div className={styles.titleContent}>
              <h1>{t('ecomOrders.createNewOrder')}</h1>
              <p>{t('ecomOrders.addNewOrderToSystem')}</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button onClick={() => navigate('/orders')} className={styles.backButton}>
              <ArrowLeft size={16} />
              {t('ecomOrders.backToOrders')}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.pageContent}>
        {!isLoadingQuota && quotaInfo && (
          <QuotaWarning
            resourceType="ecomOrders"
            usage={quotaInfo}
            severity={getQuotaSeverity()}
            dismissible={!isQuotaExceeded()}
          />
        )}

        <form onSubmit={handleSubmit} className={styles.orderForm}>
          {/* TOP 2-COLUMN: Shop+Customer | Items+Summary */}
          <div className={styles.topColumns}>
            <div className={styles.leftColumn}>
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <Store className={styles.sectionIcon} size={16} />
                  <h2>{t('ecomOrders.shopSelection')}</h2>
                </div>
                <ShopSelectionSection
                  selectedShop={selectedShop}
                  userShops={userShops}
                  onShopChange={handleShopSelect}
                  orderNumberPreview={getOrderNumberPreview()}
                  error={errors.shopId}
                  compact={true}
                />
              </div>

              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <User className={styles.sectionIcon} size={16} />
                  <h2>{t('ecomOrders.customerInformation')}</h2>
                </div>
                <CustomerInformationSection
                  formData={formData}
                  requiredFields={requiredFields}
                  cities={referenceData.cities}
                  errors={errors}
                  isCalculatingDelivery={isCalculatingDelivery}
                  onFieldChange={handleFieldChange}
                  onPhoneNumberChange={handlePhoneNumberChange}
                  onPhoneValidationChange={handlePhoneValidationChange}
                  onTogglePrimaryPhone={togglePrimaryPhone}
                  onAddPhoneNumber={addPhoneNumber}
                  onRemovePhoneNumber={removePhoneNumber}
                  onAutofillCustomer={autofillCustomer}
                  onRevertAutofill={revertAutofill}
                />
              </div>

              <div className={`${styles.formSection} ${errors.orderOriginKey ? styles.formSectionError : ''}`}>
                <div className={styles.sectionHeader}>
                  <Store className={styles.sectionIcon} size={16} />
                  <h2>{t('ecomOrders.orderOrigin') || 'Order Origin'}{requiredFields.orderOriginKey && ' *'}</h2>
                </div>
                <OrderOriginSection
                  value={formData.orderOriginKey}
                  onChange={(key) => handleFieldChange('orderOriginKey', key)}
                  error={errors.orderOriginKey}
                />
              </div>
            </div>

            <div className={styles.rightColumn}>
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <Package className={styles.sectionIcon} size={16} />
                  <h2>{t('ecomOrders.orderItems')}</h2>
                </div>
                <OrderItemsSection
                  shopId={formData.shopId}
                  orderItems={formData.orderItems}
                  errors={errors}
                  canChangeUnitPrice={canChangeUnitPrice}
                  canGiveDiscount={canGiveDiscount}
                  crossSellEnabled={!!crossSellingSettings.enabled}
                  currentUserId={user?.id}
                  createdByUserId={user?.id}
                  onAddItem={addOrderItem}
                  onUpdateItem={updateOrderItem}
                  onRemoveItem={removeOrderItem}
                  onUpdateBundleQuantity={updateBundleQuantity}
                  onRemoveBundle={removeBundle}
                  onSetBundleCrossSell={setBundleCrossSell}
                  allowDiscountOverLine={isReplacementOrder()}
                />
              </div>

              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <FileText className={styles.sectionIcon} size={16} />
                  <h2>{t('ecomOrders.orderSummary')}</h2>
                </div>
                <OrderSummarySection
                  orderTotals={orderTotals}
                  paymentRecords={formData.paymentRecords}
                  isCalculatingDelivery={isCalculatingDelivery}
                  canChangeDeliveryCharge={canChangeDeliveryCharge}
                  deliveryCharge={formData.deliveryCharge}
                  onDeliveryChargeChange={(value) => handleFieldChange('deliveryCharge', value)}
                  isStorePickup={isStorePickupOrder()}
                  weight={formData.weight}
                  onWeightChange={handleWeightChange}
                  hidePaymentSummary={true}
                  deliveryChargeError={errors.deliveryCharge}
                />
              </div>
            </div>
          </div>

          {/* BOTTOM 2-COLUMN: Status | Payment Records */}
          <div className={styles.bottomColumns}>
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <CheckCircle className={styles.sectionIcon} size={16} />
                <h2>{t('ecomOrders.orderStatusAndType')}</h2>
              </div>
              <OrderStatusSection
                formData={formData}
                referenceData={referenceData}
                selectableOrderStatuses={selectableOrderStatuses}
                errors={errors}
                onFieldChange={handleFieldChange}
                getStatusReasons={getStatusReasons}
                isReplacementOrder={isReplacementOrder()}
                replacedOrderData={replacedOrderData}
                isLookingUpOrder={isLookingUpOrder}
                orderLookupError={orderLookupError}
                isEditMode={false}
                orderTotals={orderTotals}
                onUpdateReturnItemQty={updateReturnItemQty}
              />
            </div>

            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <CreditCard className={styles.sectionIcon} size={16} />
                <h2>{t('ecomOrders.paymentRecords')}</h2>
              </div>
              {(requiresPaymentRecords() || allowsOptionalPaymentRecords()) ? (
                <PaymentRecordsSection
                  paymentRecords={formData.paymentRecords}
                  paymentMethods={referenceData.paymentMethods}
                  orderTotal={orderTotals.grandTotal}
                  errors={errors}
                  required={requiresPaymentRecords()}
                  onAddRecord={addPaymentRecord}
                  onUpdateRecord={updatePaymentRecord}
                  onRemoveRecord={removePaymentRecord}
                />
              ) : (
                <div className={styles.paymentNotRequired}>
                  <CreditCard size={28} className={styles.paymentNotRequiredIcon} />
                  <p>{t('ecomOrders.paymentRecordsNotRequired') || 'No payment records required'}</p>
                  <span>{t('ecomOrders.paymentRecordsNotRequiredHint') || 'Select a payment type that requires records to add payment details.'}</span>
                </div>
              )}
            </div>
          </div>

          {/* Spacer prevents fixed bar from covering last content row */}
          <div className={styles.actionBarSpacer} />
        </form>
      </div>

      {/* FIXED BOTTOM ACTION BAR */}
      <div className={styles.fixedActionBar}>
        <div className={styles.actionBarInner}>

          {/* ── Compact Status Controls (desktop only) ── */}
          <div className={styles.actionBarStatus}>
            <span className={styles.actionBarStatusLabel}>Status</span>

            {/* Order Status select */}
            <div className={styles.actionBarStatusDropdown} title={t('ecomOrders.orderStatus')}>
              <Dropdown
                value={formData.orderStatusKey}
                onChange={(value) => value && handleFieldChange('orderStatusKey', value)}
                options={selectableOrderStatuses.map((status) => ({ value: status.key, label: status.label }))}
                icon={<CheckCircle size={13} />}
                allowClear={false}
                size="compact"
                dropdownPosition="top"
              />
            </div>

            {/* Status Reason — shown only when selected status requiresReason */}
            {selectedStatus?.requiresReason && (
              <div className={styles.actionBarStatusDropdown} title={t('ecomOrders.statusReason')}>
                <Dropdown
                  value={formData.orderStatusReasonKey || ''}
                  onChange={(value) => handleFieldChange('orderStatusReasonKey', value || '')}
                  options={resolvedStatusReasons.map(reason => ({ value: reason.key, label: reason.label }))}
                  placeholder={t('ecomOrders.selectStatusReason')}
                  icon={<CheckCircle size={13} />}
                  size="compact"
                  dropdownPosition="top"
                />
              </div>
            )}

            {/* Hold Until Date — shown only when selected status requiresHoldDate */}
            {selectedStatus?.requiresHoldDate && (
              <div className={styles.actionBarStatusDropdown} title={t('ecomOrders.holdUntilDate')}>
                <DatePicker
                  value={formData.holdUntilDate || ''}
                  onChange={(value) => handleFieldChange('holdUntilDate', value || '')}
                  minDate={new Date().toISOString().split('T')[0]}
                  placeholder={t('ecomOrders.holdUntilDate')}
                  size="compact"
                  dropdownPosition="top"
                />
              </div>
            )}
          </div>

          {messageCenterSettings.enabled && selectedShop?.messageCenterShiftEligible && (
            <ShiftPickerSection
              value={formData.messageCenterShiftId}
              onChange={(shiftId) => handleFieldChange('messageCenterShiftId', shiftId)}
              rememberable={true}
            />
          )}

          {/* ── Meta: total + item count ── */}
          <div className={styles.actionBarMeta}>
            {orderTotals.grandTotal > 0 && (
              <span className={styles.actionBarTotal}>
                Total: <strong>{formatGrandTotal(orderTotals.grandTotal)}</strong>
              </span>
            )}
            {formData.orderItems.length > 0 && (
              <span className={styles.actionBarItemCount}>
                {formData.orderItems.length} item{formData.orderItems.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* ── Action buttons ── */}
          <div className={styles.actionBarButtons}>
            <button
              type="button"
              onClick={() => navigate('/orders')}
              className={styles.cancelButton}
              disabled={isSubmitting}
            >
              <X size={15} />
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className={styles.submitButton}
              disabled={isSubmitting || isLoading || isQuotaExceeded()}
            >
              {isSubmitting ? (
                <div className={styles.loadingSpinner} />
              ) : (
                <>
                  <Save size={15} />
                  {t('ecomOrders.createOrder')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrderPage;